// Agnes Video 2.5: https://agnes-ai.com/zh-Hans/docs/agnes-video-25
export const BOTGO_VIDEO_25_SIZES = {
  '21:9': { '720p': '1470x630', '1080p': '2206x946', '2K': '2940x1260' },
  '16:9': { '720p': '1280x720', '1080p': '1920x1080', '2K': '2560x1440' },
  '4:3': { '720p': '1112x834', '1080p': '1664x1248', '2K': '2224x1668' },
  '1:1': { '720p': '960x960', '1080p': '1440x1440', '1K': '1024x1024', '2K': '1920x1920' },
  '3:4': { '720p': '834x1112', '1080p': '1248x1664', '2K': '1668x2224' },
  '9:16': { '720p': '720x1280', '1080p': '1080x1920', '2K': '1440x2560' },
};

export const BOTGO_VIDEO_25_CAPABILITIES = {
  allowedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
  allowedResolutions: ['720p', '1080p', '1K', '2K'],
  allowedSizesByAspect: BOTGO_VIDEO_25_SIZES,
  resolutionAspectConstraints: { '1K': ['1:1'] },
  allowedDurations: Array.from({ length: 9 }, (_, i) => i + 4),
  defaultDuration: 5,
  minDurationSeconds: 4,
  maxDurationSeconds: 12,
  maxResolution: '2K',
  allowedInputModes: ['text', 'image', 'video', 'audio'],
  allowedVideoFunctionModes: ['general', 'frames', 'reference'],
  maxInputImages: 8,
  maxInputVideos: 1,
  maxInputAudios: 3,
  maxInputAssets: 12,
  maxInputImageFileSizeMb: 15,
  maxInputImageTotalFileSizeMb: 50,
  maxInputVideoFileSizeMb: 50,
  maxInputVideoDurationSeconds: 12,
  maxInputVideoTotalDurationSeconds: 12,
  maxInputAudioFileSizeMb: 15,
  maxInputAudioTotalFileSizeMb: 64,
  maxInputAudioTotalDurationSeconds: 12,
};

export const BOTGO_VIDEO_25_RATES = { '720p': 10, '1080p': 16, '1k': 16, '2k': 22 };

export const getBotgoVideo25InputDuration = (assets = []) => (Array.isArray(assets) ? assets : [])
  .filter((asset) => asset?.type === 'video')
  .reduce((sum, asset) => sum + Math.round(Math.max(0, Number(asset.durationSeconds ?? asset.duration) || 0)), 0);

export function resolveBotgoVideo25Spec({ resolutionLabel, resolution, quality, size } = {}) {
  const label = String(resolutionLabel || quality || resolution || '').toLowerCase();
  const pixels = String(size || resolution || '').toLowerCase();
  for (const [aspect, sizes] of Object.entries(BOTGO_VIDEO_25_SIZES)) {
    for (const [tier, mappedSize] of Object.entries(sizes)) {
      if (mappedSize === pixels) return { resolution: tier, aspect };
    }
  }
  const tier = Object.keys(BOTGO_VIDEO_25_RATES).find((key) => key === label) || '720p';
  return { resolution: tier === '1k' ? '1K' : tier === '2k' ? '2K' : tier, aspect: tier === '1k' ? '1:1' : '16:9' };
}

export function getBotgoVideo25CreditCost(params = {}) {
  const { resolution } = resolveBotgoVideo25Spec(params);
  const outputSeconds = Number(params.duration ?? 5);
  // Match the existing generator: round input-video seconds before charging.
  const inputSeconds = Math.round(Math.max(0, Number(params.inputVideoDuration) || 0));
  const excessImages = Math.max(0, (Number(params.inputImageCount) || 0) - 5);
  return Math.round(((Number.isFinite(outputSeconds) ? outputSeconds : 5) + inputSeconds)
    * BOTGO_VIDEO_25_RATES[resolution.toLowerCase()] + excessImages * 2);
}

const inputError = (errorKey, values = {}) => ({ errorKey, values });

export function validateBotgoVideo25InputAsset(asset = {}) {
  const { type } = asset;
  if (!['image', 'video', 'audio'].includes(type)) return inputError('botgoVideo25Media');
  const fileSize = Number(asset.fileSizeBytes);
  const maxMb = type === 'video' ? 50 : 15;
  if (!Number.isFinite(fileSize) || fileSize <= 0) return inputError('botgoVideo25Metadata');
  if (fileSize >= maxMb * 1024 * 1024) return inputError('botgoVideo25FileSize', { maxMb });
  if (type === 'image') {
    const width = Number(asset.width);
    const height = Number(asset.height);
    if (!width || !height) return inputError('botgoVideo25Metadata');
    if (width < 256 || height < 256 || width > 5760 || height > 5760) return inputError('botgoVideo25ImageSize');
  } else {
    const seconds = Number(asset.durationSeconds ?? asset.duration);
    if (!Number.isFinite(seconds) || seconds <= 0) return inputError('botgoVideo25Metadata');
    if (seconds > 12 || (type === 'video' && seconds < 2)) return inputError('botgoVideo25Duration');
    if (type === 'video') {
      const fps = Number(asset.fps);
      if (!Number.isFinite(fps) || fps < 24 || fps > 60) return inputError('botgoVideo25Fps');
    }
  }
  return null;
}

export function validateBotgoVideo25Inputs(assets = [], { functionMode = 'general' } = {}) {
  if (!['general', 'frames', 'reference'].includes(functionMode)) return inputError('botgoVideo25Mode');
  const groups = Object.fromEntries(['image', 'video', 'audio'].map((type) => [type, assets.filter((asset) => asset.type === type)]));
  const limits = functionMode === 'reference' ? { image: 8, video: 1, audio: 3 }
    : { image: functionMode === 'frames' ? 2 : 1, video: 0, audio: 0 };
  if (assets.length > 12 || Object.entries(limits).some(([type, max]) => groups[type].length > max)) return inputError('botgoVideo25Media');
  if (functionMode !== 'general' && !assets.length) return inputError('botgoVideo25ReferenceRequired');
  for (const asset of assets) {
    const error = validateBotgoVideo25InputAsset(asset);
    if (error) return error;
  }
  for (const [type, maxMb] of [['image', 50], ['audio', 64]]) {
    if (groups[type].reduce((sum, asset) => sum + Number(asset.fileSizeBytes), 0) >= maxMb * 1024 * 1024) return inputError('botgoVideo25TotalSize', { maxMb });
  }
  const audioSeconds = groups.audio.reduce((sum, asset) => sum + Number(asset.durationSeconds ?? asset.duration), 0);
  if (groups.audio.length && (audioSeconds < 2 || audioSeconds > 12)) return inputError('botgoVideo25Duration');
  return null;
}

// The studio and recreated assets use @ImageN or @image_N; Agnes uses 1-based arrays.
export const formatBotgoVideo25Prompt = (prompt = '') => String(prompt).replace(
  /@(image|图片|video|视频|audio|音频)\s*_?\s*(\d+)/gi,
  (match, type, rawIndex) => {
    const index = Number(rawIndex);
    if (index < 1) return match;
    const token = /^(image|图片)$/i.test(type) ? 'Picture' : /^(video|视频)$/i.test(type) ? 'Video' : 'Audio';
    return `<${token} ${index}>`;
  }
);
